import React, { useRef, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import SectionBackground from './SectionBackground';

const FeaturesSection: React.FC = () => {
  const { t } = useTranslation();
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const features = [
    {
      title: t('landing.features.feature1.title'),
      description: t('landing.features.feature1.description'),
      icon: '⚡',
      gradient: 'linear-gradient(135deg, #667EEA 0%, #764BA2 100%)',
      size: 'large'
    },
    {
      title: t('landing.features.feature2.title'),
      description: t('landing.features.feature2.description'),
      icon: '📋',
      gradient: 'linear-gradient(135deg, #F093FB 0%, #F5576C 100%)',
      size: 'medium'
    },
    {
      title: t('landing.features.feature3.title'),
      description: t('landing.features.feature3.description'),
      icon: '✍️',
      gradient: 'linear-gradient(135deg, #4FACFE 0%, #00F2FE 100%)',
      size: 'medium'
    },
    {
      title: t('landing.features.feature4.title'),
      description: t('landing.features.feature4.description'),
      icon: '🔄',
      gradient: 'linear-gradient(135deg, #43E97B 0%, #38F9D7 100%)',
      size: 'small'
    },
    {
      title: t('landing.features.feature5.title'),
      description: t('landing.features.feature5.description'),
      icon: '🔒',
      gradient: 'linear-gradient(135deg, #FA709A 0%, #FEE140 100%)',
      size: 'small'
    },
    {
      title: t('landing.features.feature6.title'),
      description: t('landing.features.feature6.description'),
      icon: '💬',
      gradient: 'linear-gradient(135deg, #30CFD0 0%, #330867 100%)',
      size: 'small'
    }
  ];

  return (
    <SectionBackground
      ref={sectionRef}
      style={{
        padding: '120px 20px'
      }}
    >

      <div className="container" style={{ maxWidth: '1400px', margin: '0 auto', position: 'relative', zIndex: 1 }}>
        {/* Section header */}
        <div style={{ textAlign: 'center', marginBottom: '80px' }}>
          <div style={{
            display: 'inline-block',
            padding: '8px 20px',
            background: 'rgba(99, 102, 241, 0.1)',
            border: '1px solid rgba(99, 102, 241, 0.3)',
            borderRadius: '100px',
            marginBottom: '30px'
          }}>
            <span style={{
              color: '#6366F1',
              fontSize: '0.9rem',
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '2px'
            }}>
              {t('landing.features.title')}
            </span>
          </div>
          
          <h2 style={{
            fontSize: 'clamp(2.5rem, 4vw, 3.5rem)',
            fontWeight: 900,
            marginBottom: '20px',
            color: 'white'
          }}>
{t('landing.features.heading')}
            <br />
            <span style={{
              background: 'linear-gradient(90deg, #6366F1 0%, #A855F7 50%, #EC4899 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text'
            }}>
              {t('landing.features.subheading')}
            </span>
          </h2>
        </div>

        {/* Bento grid */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(6, 1fr)',
          gridTemplateRows: 'repeat(2, 300px)',
          gap: '20px',
          maxWidth: '1200px',
          margin: '0 auto'
        }}>
          {features.map((feature, index) => {
            const gridSpan = 
              feature.size === 'large' ? { gridColumn: 'span 3', gridRow: 'span 2' } :
              feature.size === 'medium' ? { gridColumn: 'span 3', gridRow: 'span 1' } :
              { gridColumn: 'span 2', gridRow: 'span 1' };

            return (
              <div
                key={index}
                style={{
                  ...gridSpan,
                  background: hoveredIndex === index ? feature.gradient : 'rgba(255, 255, 255, 0.02)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  borderRadius: '30px',
                  padding: '40px',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                  cursor: feature.size === 'large' ? 'default' : 'pointer',
                  transition: 'all 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
                  transform: isVisible ? 'translateY(0)' : 'translateY(30px)',
                  opacity: isVisible ? 1 : 0,
                  transitionDelay: `${index * 0.1}s`,
                  position: 'relative',
                  overflow: 'hidden'
                }}
                onMouseEnter={() => setHoveredIndex(index)}
                onMouseLeave={() => setHoveredIndex(null)}
              >
                {/* Glow effect */}
                {hoveredIndex === index && (
                  <div style={{
                    position: 'absolute',
                    top: '-50%',
                    left: '-50%',
                    width: '200%',
                    height: '200%',
                    background: 'radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 70%)',
                    animation: 'rotate 3s linear infinite'
                  }} />
                )}

                <div style={{ position: 'relative', zIndex: 1 }}>
                  <div style={{
                    fontSize: feature.size === 'large' ? '4rem' : '3rem',
                    marginBottom: '20px',
                    filter: hoveredIndex === index ? 'drop-shadow(0 0 20px rgba(255,255,255,0.5))' : 'none',
                    transition: 'all 0.3s ease'
                  }}>
                    {feature.icon}
                  </div>
                  
                  <h3 style={{
                    fontSize: feature.size === 'large' ? '2rem' : '1.5rem',
                    fontWeight: 700,
                    marginBottom: '15px',
                    color: hoveredIndex === index ? 'white' : 'rgba(255, 255, 255, 0.9)'
                  }}>
                    {feature.title}
                  </h3>
                  
                  <p style={{
                    fontSize: feature.size === 'large' ? '1.2rem' : '1rem',
                    color: hoveredIndex === index ? 'rgba(255, 255, 255, 0.9)' : 'rgba(255, 255, 255, 0.6)',
                    lineHeight: 1.6
                  }}>
                    {feature.description}
                  </p>
                </div>

                {feature.size === 'large' && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      navigate('/signup');
                    }}
                    style={{
                      marginTop: '30px',
                      padding: '15px 30px',
                      background: hoveredIndex === index ? 'rgba(255, 255, 255, 0.2)' : 'rgba(255, 255, 255, 0.05)',
                      border: '1px solid rgba(255, 255, 255, 0.2)',
                      borderRadius: '12px',
                      color: 'white',
                      fontWeight: 600,
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      alignSelf: 'flex-start',
                      position: 'relative',
                      zIndex: 10
                    }}
                  >
                    Get Started →
                  </button>
                )}
              </div>
            );
          })}
        </div>
      </div>

      <style>{`
        @keyframes rotate {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        @media (max-width: 768px) {
          .container > div:last-child {
            grid-template-columns: 1fr !important;
            grid-template-rows: auto !important;
          }
          .container > div:last-child > div {
            grid-column: span 1 !important;
            grid-row: span 1 !important;
          }
        }
      `}</style>
    </SectionBackground>
  );
};

export default FeaturesSection; 